import { DOCUMENT } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UiService, WorkspaceTheme } from '@keeps-platform-frontend-workspace/kp-keeps';
import chroma from 'chroma-js';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';
import { AppConfig, defaultConfig } from '../config/fuse-config';
import { FuseConfigService, FuseMediaWatcherService, FusePlatformService } from '../services';
import { NavigationService } from '../services/navigation/navigation.service';
import { FUSE_VERSION } from '../version';
import { Layout } from './layout.types';
import { generateTheme, KeepsTheme } from './colors/color-utils';

@Component({
  selector: 'fuse-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  @Input() enableGlobalSearch = false;
  @Input() fullscreenTooltip = '';
  @Output() logoClick = new EventEmitter<void>();
  config: AppConfig;
  layout: Layout;
  scheme: 'dark' | 'light';
  themeColor: string;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private _document: Document,
    private _renderer2: Renderer2,
    private _router: Router,
    private _fuseConfigService: FuseConfigService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fusePlatformService: FusePlatformService,
    private _uiService: UiService,
    private _navigationService: NavigationService,
  ) {
    this._uiService.workspaceTheme$
      .pipe(
        filter((theme) => !!theme),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(({ customColor, isDark, iconUrl }: WorkspaceTheme) => {
        const themeColor = customColor || defaultConfig.themeColor;
        this._fuseConfigService.config = { themeColor, scheme: isDark ? 'dark' : 'light' };
        this._navigationService.setLogo(iconUrl);
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the theme and scheme based on the configuration
    combineLatest([
      this._fuseConfigService.config$,
      this._fuseMediaWatcherService.onMediaQueryChange$([
        '(prefers-color-scheme: dark)',
        '(prefers-color-scheme: light)',
      ]),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            themeColor: config.themeColor,
          };

          // If the scheme is set to 'auto'...
          if (config.scheme === 'auto') {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
          }

          return options;
        }),
      )
      .subscribe((options) => {
        // Update the options only if at least one has changed
        if (options.scheme === this.scheme && options.themeColor === this.themeColor) {
          return;
        }

        this.scheme = options.scheme;
        this.themeColor = options.themeColor;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
      });

    // Subscribe to config changes
    this._fuseConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: AppConfig) => {
      // Store the config
      this.config = config;

      // Update the layout
      this._updateLayout();
    });

    // Subscribe to NavigationEnd event
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => {
        // Update the layout
        this._updateLayout();
      });

    // Set the app version
    this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

    // Set the OS name
    this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot?.queryParamMap.get('layout') as Layout;
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      // Check if there is a 'layout' data
      if (path?.routeConfig?.data?.['layout']) {
        // Set the layout
        this.layout = path.routeConfig.data['layout'];
      }
    });
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    // Remove class names for all schemes
    this._document.body.classList.remove('light', 'dark');

    // Add class name for the currently selected scheme
    setTimeout(() => {
      this._document.body.classList.add(this.scheme);
    }, 0);
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    this._updateThemeColors();
  }

  private _updateThemeColors(): void {
    const theme = generateTheme(this.themeColor);

    const palettes = ['primary', 'accent', 'warn'];
    let rule = '';

    for (const palette of palettes) {
      const { contrast, ...colors } = theme[palette as keyof KeepsTheme];

      rule += `--kp-${palette}: ${String(colors['500'])};\n`;
      rule += `--kp-${palette}-rgb: ${chroma(String(colors['500'])).rgb().join(',')};\n`;

      Object.entries(colors).forEach(([key, color]) => {
        rule += `--kp-${palette}-${key}: ${String(color)};\n`;
        rule += `--kp-${palette}-${key}-rgb: ${chroma(String(color)).rgb().join(',')};\n`;
      });

      rule += `--kp-on-${palette}: ${String(contrast['500'])};\n`;
      rule += `--kp-on-${palette}-rgb: ${chroma(String(contrast['500'])).rgb().join(',')};\n`;

      Object.entries(contrast).forEach(([key, color]) => {
        rule += `--kp-on-${palette}-${key}: ${String(color)};\n`;
        rule += `--kp-on-${palette}-${key}-rgb: ${chroma(String(color)).rgb().join(',')};\n`;
      });
    }

    const colorPalette = document.createElement('style');
    colorPalette.id = 'color-palette';
    colorPalette.textContent = `:root{${rule}}`;

    const currentElement = document.getElementById('color-palette');
    if (currentElement) {
      document.head.replaceChild(colorPalette, currentElement);
    } else {
      document.head.appendChild(colorPalette);
    }
  }
}
